import { useMemo, useState } from 'react';
import {
  useCreateMtoLotMutation,
  useCreateMtoQualityDocMutation,
  useExtractMtoQualityBatchMutation,
  useListMtoLotsQuery,
} from '@shared/api';
import type { MaterialLot, NewMaterialLot, QualityDocType } from '@entities/mto';
import { apiErrorMessage, QUALITY_DOC_TYPES } from '@entities/mto';
import { ACCEPT_ANY } from '@shared/lib/fileAccept';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { FileDropZone } from '@shared/ui/FileDropZone';
import {
  bulkStatusLabel,
  bulkStatusTone,
  isImportable,
  itemToRow,
  lotLabel,
  normKey,
  skipReason,
} from '../helpers';
import {
  CellInput,
  CellSelect,
  ErrBox,
  FileCell,
  FileNameText,
  ImportBar,
  InlineHint,
  Muted,
  NewLotFields,
  PartNote,
  RowNote,
  SectionTitle,
  SummaryLine,
  TabHint,
  Table,
  TableScroll,
  Wrap,
} from '../styles';
import type { BulkRow } from '../types';
import { NEW_LOT, SKIP_ROW } from '../types';

/**
 * Вкладка «Групповая загрузка»: массовая загрузка документов качества с
 * AI-автоклассификацией. Файлы (любой формат) → `extract-batch` (тип + реквизиты
 * + подсказка партии по каждому) → редактируемая таблица предпросмотра → импорт
 * одной кнопкой (создание партий `createLot` + документов `createQualityDoc`).
 * Ничего не пишется до кнопки «Импортировать» — предпросмотр и есть гейт HITL.
 */
export function BulkUploadTab({ canWrite }: { canWrite: boolean }) {
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<{ done: number; failed: number; skipped: number } | null>(
    null,
  );

  const [extractBatch, { isLoading: extracting }] = useExtractMtoQualityBatchMutation();
  const [createLot] = useCreateMtoLotMutation();
  const [createDoc] = useCreateMtoQualityDocMutation();
  const lotsQ = useListMtoLotsQuery();
  const lots = useMemo<MaterialLot[]>(() => lotsQ.data?.items ?? [], [lotsQ.data]);

  const importableCount = useMemo(() => rows.filter(isImportable).length, [rows]);

  function patchRow(key: string, patch: Partial<BulkRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  /** Файлы из зоны загрузки: перетаскивание, выбор в диалоге, папка целиком
   *  или архив — `FileDropZone` сводит все способы к плоскому списку. */
  async function handlePick(picked: File[]) {
    if (picked.length === 0) return;
    setBanner(null);
    setSummary(null);
    const form = new FormData();
    picked.forEach((f) => form.append('file', f, f.name));
    try {
      const res = await extractBatch(form).unwrap();
      setFiles(picked);
      setRows(res.items.map(itemToRow));
    } catch (err) {
      setBanner(apiErrorMessage(err) ?? 'Не удалось распознать файлы. Попробуйте ещё раз.');
      // eslint-disable-next-line no-console
      console.error('extractMtoQualityBatch failed', err);
    }
  }

  function resetAll() {
    setFiles([]);
    setRows([]);
    setSummary(null);
    setBanner(null);
  }

  async function importAll() {
    setBanner(null);
    setImporting(true);
    // Дедуп новых партий в пределах одного импорта: два документа одной новой
    // партии не должны создать две партии.
    const createdLots = new Map<string, string>();
    let done = 0;
    let failed = 0;
    let skipped = 0;
    for (const r of rows) {
      if (r.status === 'done') {
        done += 1;
        continue; // повторный импорт (ретрай) не дублирует уже загруженное
      }
      if (!isImportable(r)) {
        patchRow(r.key, { status: 'skip', resultMsg: skipReason(r) });
        skipped += 1;
        continue;
      }
      patchRow(r.key, { status: 'importing', resultMsg: null });
      try {
        let lotId: string;
        if (r.lotChoice === NEW_LOT) {
          const key = normKey(r.lotNo) || normKey(r.nomenclature);
          const cached = key ? createdLots.get(key) : undefined;
          if (cached) {
            lotId = cached;
          } else {
            const body: NewMaterialLot = { nomenclature: r.nomenclature.trim() };
            if (r.lotNo.trim()) body.lot_no = r.lotNo.trim();
            const lotRes = await createLot(body).unwrap();
            lotId = lotRes.lot.id;
            if (key) createdLots.set(key, lotId);
          }
        } else {
          lotId = r.lotChoice;
        }
        const file = files[r.index];
        const form = new FormData();
        form.append('lot_id', lotId);
        form.append('doc_type', r.docType);
        if (r.number.trim()) form.append('number', r.number.trim());
        if (r.docDate.trim()) form.append('doc_date', r.docDate.trim());
        if (r.issuer.trim()) form.append('issuer', r.issuer.trim());
        if (r.validUntil.trim()) form.append('valid_until', r.validUntil.trim());
        if (r.nomenclature.trim()) form.append('nomenclature', r.nomenclature.trim());
        // Реквизиты распознаны ИИ — черновик до «Подтвердить» (в проверки
        // действительности и дефицита идут только подтверждённые).
        form.append('status', 'draft');
        if (file) form.append('file', file, r.filename);
        // Документ из пачки в одном PDF — к нему уходят только его страницы.
        if (file && r.pages) form.append('pages', `${r.pages[0]}-${r.pages[1]}`);
        await createDoc({ lotId, form }).unwrap();
        patchRow(r.key, { status: 'done', resultMsg: 'импортирован черновиком' });
        done += 1;
      } catch (err) {
        patchRow(r.key, { status: 'error', resultMsg: apiErrorMessage(err) ?? 'ошибка импорта' });
        failed += 1;
        // eslint-disable-next-line no-console
        console.error('bulk import row failed', err);
      }
    }
    setImporting(false);
    setSummary({ done, failed, skipped });
  }

  const busy = extracting || importing;

  return (
    <Wrap>
      <Card>
        <SectionTitle>Групповая загрузка документов качества</SectionTitle>
        <TabHint>
          Загрузите сразу несколько паспортов, сертификатов и деклараций в любом формате (PDF, скан,
          фото, Excel, Word). Нейросеть определит тип каждого документа, извлечёт реквизиты и подберёт
          партию — вам останется проверить и импортировать всё одной кнопкой. Пачку документов в
          одном PDF (например, 30 паспортов от поставщика) помощник разделит по страницам сам: у
          каждого документа будет своя строка, и в документ сохранятся только его страницы.
        </TabHint>
        {canWrite && (
          <FileDropZone
            multiple
            compact
            accept={ACCEPT_ANY}
            disabled={busy}
            primary={extracting ? 'Распознаю документы…' : undefined}
            hint="Или нажмите, чтобы выбрать. Тип документа определит нейросеть — вручную указывать не нужно."
            onFiles={handlePick}
          />
        )}
        {!canWrite && <InlineHint>Только просмотр — загрузка недоступна.</InlineHint>}
        {banner && <ErrBox>{banner}</ErrBox>}
      </Card>

      {rows.length > 0 && (
        <Card>
          <SectionTitle>
            Предпросмотр <Badge $tone="neutral">{rows.length}</Badge>
          </SectionTitle>
          <TabHint>
            Проверьте распознанное. Тип и реквизиты можно поправить прямо в таблице. Для новой партии
            («+ создать новую») укажите наименование — она заведётся автоматически при импорте.
          </TabHint>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Файл</th>
                  <th scope="col">Тип</th>
                  <th scope="col">Номер</th>
                  <th scope="col">Дата выдачи</th>
                  <th scope="col">Действует до</th>
                  <th scope="col">Орган</th>
                  <th scope="col">Партия</th>
                  <th scope="col">Статус</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const locked = r.status === 'done' || r.status === 'importing';
                  return (
                    <tr key={r.key}>
                      <td>
                        <FileCell>
                          <FileNameText>{r.filename}</FileNameText>
                          {r.parts > 1 && (
                            <PartNote>
                              документ {r.part} из {r.parts}
                              {r.pages
                                ? r.pages[0] === r.pages[1]
                                  ? ` · стр. ${r.pages[0]}`
                                  : ` · стр. ${r.pages[0]}–${r.pages[1]}`
                                : ''}
                            </PartNote>
                          )}
                          {r.note && <RowNote>{r.note}</RowNote>}
                        </FileCell>
                      </td>
                      <td>
                        <CellSelect
                          aria-label={`Тип документа для ${r.filename}`}
                          value={r.docType}
                          disabled={locked}
                          onChange={(e) =>
                            patchRow(r.key, { docType: e.target.value as '' | QualityDocType })
                          }
                        >
                          <option value="">— выбрать —</option>
                          {QUALITY_DOC_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </CellSelect>
                      </td>
                      <td>
                        <CellInput
                          aria-label={`Номер документа для ${r.filename}`}
                          value={r.number}
                          disabled={locked}
                          placeholder="№"
                          onChange={(e) => patchRow(r.key, { number: e.target.value })}
                        />
                      </td>
                      <td>
                        <CellInput
                          aria-label={`Дата выдачи для ${r.filename}`}
                          value={r.docDate}
                          disabled={locked}
                          placeholder="дд.мм.гггг"
                          onChange={(e) => patchRow(r.key, { docDate: e.target.value })}
                        />
                      </td>
                      <td>
                        <CellInput
                          aria-label={`Срок действия для ${r.filename}`}
                          value={r.validUntil}
                          disabled={locked}
                          placeholder="бессрочно"
                          onChange={(e) => patchRow(r.key, { validUntil: e.target.value })}
                        />
                      </td>
                      <td>
                        <CellInput
                          aria-label={`Орган выдачи для ${r.filename}`}
                          value={r.issuer}
                          disabled={locked}
                          placeholder="кем выдан"
                          onChange={(e) => patchRow(r.key, { issuer: e.target.value })}
                        />
                      </td>
                      <td>
                        <CellSelect
                          aria-label={`Партия для ${r.filename}`}
                          value={r.lotChoice}
                          disabled={locked}
                          onChange={(e) => patchRow(r.key, { lotChoice: e.target.value })}
                        >
                          <option value={NEW_LOT}>+ создать новую партию</option>
                          <option value={SKIP_ROW}>— пропустить —</option>
                          {lots.map((l) => (
                            <option key={l.id} value={l.id}>
                              {lotLabel(l)}
                            </option>
                          ))}
                        </CellSelect>
                        {r.lotChoice === NEW_LOT && (
                          <NewLotFields>
                            <CellInput
                              aria-label={`Наименование новой партии для ${r.filename}`}
                              value={r.nomenclature}
                              disabled={locked}
                              placeholder="наименование материала *"
                              onChange={(e) => patchRow(r.key, { nomenclature: e.target.value })}
                            />
                            <CellInput
                              aria-label={`Номер новой партии для ${r.filename}`}
                              value={r.lotNo}
                              disabled={locked}
                              placeholder="№ партии (необязательно)"
                              onChange={(e) => patchRow(r.key, { lotNo: e.target.value })}
                            />
                          </NewLotFields>
                        )}
                        {r.lotChoice !== NEW_LOT &&
                          r.lotChoice !== SKIP_ROW &&
                          r.matchedBy === 'nomenclature' && (
                            <InlineHint>подобрано по наименованию — проверьте</InlineHint>
                          )}
                      </td>
                      <td>
                        <Badge $tone={bulkStatusTone(r.status)}>{bulkStatusLabel(r)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>

          <ImportBar>
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              disabled={!canWrite || busy || importableCount === 0}
              onClick={importAll}
            >
              {importing ? 'Импортирую…' : `Импортировать всё (${importableCount})`}
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={busy}
              onClick={resetAll}
            >
              Очистить
            </Button>
            {summary && (
              <SummaryLine>
                <Badge $tone="success">импортировано: {summary.done}</Badge>
                {summary.failed > 0 && <Badge $tone="danger">ошибок: {summary.failed}</Badge>}
                {summary.skipped > 0 && <Badge $tone="warning">пропущено: {summary.skipped}</Badge>}
                <Muted as="span">Готовые документы — во вкладке «Партии и документы».</Muted>
              </SummaryLine>
            )}
          </ImportBar>
        </Card>
      )}
    </Wrap>
  );
}
